import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  IconButton,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  BookOnline as BookOnlineIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  GroupAdd as GroupAddIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { amenityAPI, amenityScheduleAPI, memberProfileAPI, equipmentAPI, tenantSettingsAPI } from '../../services/api';
import { Amenity, MemberProfile, BookingGuest, Equipment, UserRole } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import { differenceInDays, addDays } from 'date-fns';

interface BookAmenityDialogProps {
  open: boolean;
  onClose: () => void;
  amenity: Amenity | null;
  onSuccess: (message?: string) => void;
  preselectedDate?: Date | null;
  preselectedSlot?: { startTime: string; endTime: string } | null;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  status: string;
  pricingTier?: string;
  scheduleConstraints?: {
    courtUseRestrictions: boolean;
    minPlayers: number;
    maxPlayers: number;
    minBookingDurationMinutes: number;
    maxBookingDurationMinutes: number;
  };
}

const BookAmenityDialog: React.FC<BookAmenityDialogProps> = ({
  open,
  onClose,
  amenity,
  onSuccess,
  preselectedDate = null,
  preselectedSlot = null
}) => {
  const { user, selectedMemberProfile } = useAuth();
  const isMemberUser = user?.role === UserRole.MEMBER;
  const guestSectionRef = useRef<HTMLDivElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [bookingDate, setBookingDate] = useState<Date | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<TimeSlot[]>([]); // NEW: Track selected slots
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]); // NEW: Available slots from API
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Duration constraints - will be fetched from schedule
  const [minDurationMinutes, setMinDurationMinutes] = useState(15);
  const [maxDurationMinutes, setMaxDurationMinutes] = useState(120); // Default 2 hours

  // Guest and player count restriction states
  const [guests, setGuests] = useState<BookingGuest[]>([]);
  const [playerCountRestrictions, setPlayerCountRestrictions] = useState<{
    enabled: boolean;
    minPlayers: number;
    maxPlayers: number;
  }>({ enabled: false, minPlayers: 1, maxPlayers: 2 });

  // Equipment states
  const [availableEquipment, setAvailableEquipment] = useState<Equipment[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Array<{ equipment: Equipment; quantity: number }>>([]);
  const [equipmentAvailability, setEquipmentAvailability] = useState<Map<string, { available: number; total: number }>>(new Map());
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fee breakdown state
  const [feeBreakdown, setFeeBreakdown] = useState<any | null>(null);
  const [loadingFees, setLoadingFees] = useState(false);
  const [bookingOpenTime, setBookingOpenTime] = useState<string>('00:01');

  // Fetch member profiles and equipment when dialog opens
  useEffect(() => {
    if (open) {
      fetchMemberProfiles();
      fetchEquipmentForAmenity();
      fetchBookingOpenTime();
      resetForm();

      // If user is a member, auto-select their profile
      if (isMemberUser && selectedMemberProfile) {
        setSelectedMember(selectedMemberProfile);
      }

      // Set preselected date if provided
      if (preselectedDate) {
        setBookingDate(preselectedDate);
      }
    }
  }, [open, preselectedDate, isMemberUser, selectedMemberProfile]);


  // Fetch available slots when date changes or member is selected
  useEffect(() => {
    if (open && amenity && bookingDate && selectedMember) {
      fetchAvailableSlots();
    }
  }, [bookingDate, amenity, open, selectedMember]);

  // Check equipment availability when slots or equipment selection changes
  useEffect(() => {
    if (open && bookingDate && selectedSlots.length > 0 && selectedEquipment.length > 0) {
      checkEquipmentAvailabilityForSlots();
    }
  }, [selectedSlots, selectedEquipment, open, bookingDate]);

  // Auto-select preselected slot when available slots are loaded
  useEffect(() => {
    if (open && preselectedSlot && availableSlots.length > 0 && selectedSlots.length === 0) {
      // Find and auto-select the preselected slot
      const matchingSlot = availableSlots.find(
        slot => slot.startTime === preselectedSlot.startTime && slot.endTime === preselectedSlot.endTime
      );

      if (matchingSlot) {
        setSelectedSlots([matchingSlot]);
        deriveConstraintsFromSlots([matchingSlot]);
      }
    }
  }, [open, preselectedSlot, availableSlots]);

  // Scroll to guest section when guests are added (for mobile view)
  useEffect(() => {
    if (guests.length > 0 && guestSectionRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        guestSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [guests.length]);

  // Scroll to top when error is set
  useEffect(() => {
    if (error && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  // Fetch fee breakdown when booking parameters change
  useEffect(() => {
    if (open && amenity && selectedMember && bookingDate && selectedSlots.length > 0) {
      fetchFeeBreakdown();
    } else {
      setFeeBreakdown(null);
    }
  }, [open, amenity, selectedMember, bookingDate, selectedSlots, guests, selectedEquipment]);

  const fetchFeeBreakdown = async () => {
    if (!amenity || !selectedMember || !bookingDate || selectedSlots.length === 0) return;

    try {
      setLoadingFees(true);
      const dateString = formatDateForAPI(bookingDate);
      const sortedSlots = [...selectedSlots].sort((a, b) => a.startTime.localeCompare(b.startTime));
      const startTime = sortedSlots[0].startTime;
      const endTime = sortedSlots[sortedSlots.length - 1].endTime;

      const response = await amenityAPI.calculateAmenityBookingFees(amenity.id, {
        bookingDate: dateString,
        startTime,
        endTime,
        memberProfileId: selectedMember.id,
        guests: guests.length > 0 ? guests : undefined
      });

      setFeeBreakdown(response.data.feeBreakdown);
    } catch (error: any) {
      console.error('Error fetching fee breakdown:', error);
      // Don't set error state - fee breakdown is supplementary
    } finally {
      setLoadingFees(false);
    }
  };

  const fetchBookingOpenTime = async () => {
    try {
      const response = await tenantSettingsAPI.getSetting('bookingOpenTime');
      // API returns { key, value }, so extract the value
      const timeValue = response.data?.value || response.data || '00:01';
      setBookingOpenTime(typeof timeValue === 'string' ? timeValue : '00:01');
    } catch (error: any) {
      console.error('Error fetching booking open time:', error);
      // Use default if fetch fails
      setBookingOpenTime('00:01');
    }
  };

  const resetForm = () => {
    setSelectedMember(null);
    setBookingDate(null);
    setSelectedSlots([]);
    setAvailableSlots([]);
    setNotes('');
    setError(null);
    setGuests([]);
    setPlayerCountRestrictions({ enabled: false, minPlayers: 1, maxPlayers: 2 });
    setSelectedEquipment([]);
    setEquipmentAvailability(new Map());
    setFeeBreakdown(null);
  };

  // State for max booking date from backend
  const [maxBookingDate, setMaxBookingDate] = useState<Date>(() => {
    const defaultMax = new Date();
    defaultMax.setMonth(defaultMax.getMonth() + 3);
    return defaultMax;
  });
  const [loadingMaxDate, setLoadingMaxDate] = useState(false);

  // Fetch max booking date from backend when member or amenity changes
  useEffect(() => {
    const fetchMaxBookingDate = async () => {
      if (!selectedMember || !amenity) {
        // Default to 3 months if no member selected
        const defaultMax = new Date();
        defaultMax.setMonth(defaultMax.getMonth() + 3);
        setMaxBookingDate(defaultMax);
        return;
      }

      try {
        setLoadingMaxDate(true);
        const response = await amenityAPI.getMaxBookingDate(amenity.id, selectedMember.id);
        
        if (response.data.unlimited || !response.data.maxBookingDate) {
          // Unlimited or no restriction, allow 3 months
          const unlimitedMax = new Date();
          unlimitedMax.setMonth(unlimitedMax.getMonth() + 3);
          setMaxBookingDate(unlimitedMax);
        } else {
          setMaxBookingDate(new Date(response.data.maxBookingDate));
        }
      } catch (error) {
        console.error('Error fetching max booking date:', error);
        // Fallback to 3 months on error
        const fallbackMax = new Date();
        fallbackMax.setMonth(fallbackMax.getMonth() + 3);
        setMaxBookingDate(fallbackMax);
      } finally {
        setLoadingMaxDate(false);
      }
    };

    fetchMaxBookingDate();
  }, [selectedMember, amenity]);

  // Check if a date should be disabled in the date picker
  const shouldDisableDate = (date: Date): boolean => {
    // Check if the date is beyond the max booking date (compare date-only)
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    const maxDateOnly = new Date(maxBookingDate);
    maxDateOnly.setHours(0, 0, 0, 0);

    return dateOnly > maxDateOnly;
  };

  const fetchMemberProfiles = async () => {
    try {
      setLoadingMembers(true);
      const response = await memberProfileAPI.getAllMemberProfiles();
      // Filter to only active members
      const activeMembers = response.data.filter((member: MemberProfile) =>
        member.status === 'active'
      );

      activeMembers.sort((a: MemberProfile, b: MemberProfile) => {
        const firstNameCompare = a.firstName.localeCompare(b.firstName);
        if (firstNameCompare !== 0) return firstNameCompare;
        return a.lastName.localeCompare(b.lastName);
      });
      
      setMemberProfiles(activeMembers);
    } catch (error: any) {
      console.error('Error fetching member profiles:', error);
      setError('Failed to fetch member profiles');
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchEquipmentForAmenity = async () => {
    if (!amenity) return;

    try {
      setLoadingEquipment(true);
      const response = await equipmentAPI.getForAmenity(amenity.id);
      // Filter to only active equipment
      const activeEquipment = response.data.filter((eq: Equipment) => eq.active);
      setAvailableEquipment(activeEquipment);
    } catch (error: any) {
      console.error('Error fetching equipment:', error);
      // Don't set error state - equipment is optional
    } finally {
      setLoadingEquipment(false);
    }
  };

  const checkEquipmentAvailabilityForSlots = async () => {
    if (!bookingDate || selectedSlots.length === 0 || selectedEquipment.length === 0) return;

    const sortedSlots = [...selectedSlots].sort((a, b) => a.startTime.localeCompare(b.startTime));
    const startTime = sortedSlots[0].startTime;
    const endTime = sortedSlots[sortedSlots.length - 1].endTime;
    const dateString = formatDateForAPI(bookingDate);

    const availabilityMap = new Map<string, { available: number; total: number }>();

    for (const { equipment } of selectedEquipment) {
      try {
        // API call to check availability - we'll need to add this endpoint
        const response = await equipmentAPI.checkAvailability(equipment.id, dateString, startTime, endTime);
        availabilityMap.set(equipment.id, response.data);
      } catch (error) {
        console.error(`Failed to check availability for ${equipment.name}:`, error);
        // Set as unavailable on error
        availabilityMap.set(equipment.id, { available: 0, total: equipment.quantity });
      }
    }

    setEquipmentAvailability(availabilityMap);
  };

  // NEW: Fetch available slots when date changes
  const fetchAvailableSlots = async () => {
    if (!amenity || !bookingDate || !selectedMember) return;

    try {
      setLoadingSlots(true);
      const dateString = formatDateForAPI(bookingDate);
      const response = await amenityScheduleAPI.getAvailableSlots(amenity.id, dateString, selectedMember.id);

      if (response.data) {
        // Filter to only available slots
        let available = (response.data.slots || []).filter((slot: any) => slot.status === 'available');

        // Filter out past time slots if booking date is today
        const now = new Date();
        const bookingDateObj = new Date(bookingDate);
        const isToday = bookingDateObj.toDateString() === now.toDateString();

        if (isToday) {
          // For today, filter out slots that have already started
          const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
          available = available.filter((slot: TimeSlot) => {
            const slotStartMinutes = timeToMinutes(slot.startTime);
            return slotStartMinutes > currentTimeMinutes;
          });
        }

        setAvailableSlots(available);
        setMaxDurationMinutes(response.data.maxBookingDuration || 120);
        setMinDurationMinutes(response.data.minBookingDuration || 15);

        // No slots selected yet — reset player restrictions
        setPlayerCountRestrictions({ enabled: false, minPlayers: 1, maxPlayers: 2 });
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setError('Failed to fetch available time slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Derive effective player restrictions synchronously from the scheduleConstraints
  // embedded in each slot. Takes the most restrictive values across all selected slots
  // that have courtUseRestrictions enabled — handles cross-boundary (normal→peak) bookings
  // correctly without any additional API calls.
  const deriveConstraintsFromSlots = (slots: TimeSlot[]) => {
    let enabled = false;
    let minPlayers = 1;
    let maxPlayers = 99;

    for (const slot of slots) {
      const c = slot.scheduleConstraints;
      if (c?.courtUseRestrictions) {
        enabled = true;
        minPlayers = Math.max(minPlayers, c.minPlayers);
        maxPlayers = Math.min(maxPlayers, c.maxPlayers);
      }
    }

    setPlayerCountRestrictions({
      enabled,
      minPlayers,
      maxPlayers: maxPlayers === 99 ? 2 : maxPlayers,
    });
  };

  // NEW: Handle slot selection/deselection
  const toggleSlotSelection = async (slot: TimeSlot) => {
    setError(null);

    const isSelected = selectedSlots.some(s => s.startTime === slot.startTime && s.endTime === slot.endTime);

    let newSlots: TimeSlot[];

    if (isSelected) {
      // Deselect slot
      newSlots = selectedSlots.filter(s => !(s.startTime === slot.startTime && s.endTime === slot.endTime));
      setSelectedSlots(newSlots);
    } else {
      // Check if adding this slot would exceed max duration
      newSlots = [...selectedSlots, slot].sort((a, b) => a.startTime.localeCompare(b.startTime));

      // Validate slots are consecutive
      if (!areSlotsConsecutive(newSlots)) {
        setError('Please select consecutive time slots');
        return;
      }

      // Calculate total duration
      const totalDuration = calculateSlotsDuration(newSlots);

      // For mixed-tier bookings (peak + non-peak slots), apply the max duration limit only
      // to the peak portion — matching backend logic in bookAmenity.
      // Each slot carries its own scheduleConstraints.maxBookingDurationMinutes so we read
      // the limit directly from the peak slots instead of relying on a shared state variable.
      const peakSlots = newSlots.filter(s => s.pricingTier === 'peak');
      const hasNonPeakInNew = newSlots.some(s => s.pricingTier !== 'peak');
      const isMixedTierNew = peakSlots.length > 0 && hasNonPeakInNew;

      if (isMixedTierNew) {
        const peakMax = peakSlots[0].scheduleConstraints?.maxBookingDurationMinutes ?? maxDurationMinutes;
        const peakMinutes = peakSlots.reduce(
          (sum, s) => sum + timeToMinutes(s.endTime) - timeToMinutes(s.startTime), 0
        );
        if (peakMinutes > peakMax) {
          setError(`Maximum peak-time booking duration is ${peakMax} minutes`);
          return;
        }
      } else {
        const slotMax = newSlots[0]?.scheduleConstraints?.maxBookingDurationMinutes ?? maxDurationMinutes;
        if (totalDuration > slotMax) {
          setError(`Maximum booking duration is ${slotMax} minutes`);
          return;
        }
      }

      setSelectedSlots(newSlots);
    }

    // Derive player restrictions synchronously from the embedded slot constraints —
    // no extra API call needed, works correctly for cross-boundary (normal→peak) bookings.
    deriveConstraintsFromSlots(newSlots);
  };

  // NEW: Check if slots are consecutive
  const areSlotsConsecutive = (slots: TimeSlot[]): boolean => {
    if (slots.length <= 1) return true;

    for (let i = 0; i < slots.length - 1; i++) {
      if (slots[i].endTime !== slots[i + 1].startTime) {
        return false;
      }
    }
    return true;
  };

  // NEW: Calculate total duration of selected slots
  const calculateSlotsDuration = (slots: TimeSlot[]): number => {
    if (slots.length === 0) return 0;

    const firstSlot = slots[0];
    const lastSlot = slots[slots.length - 1];

    const startMinutes = timeToMinutes(firstSlot.startTime);
    const endMinutes = timeToMinutes(lastSlot.endTime);

    return endMinutes - startMinutes;
  };

  const handleStartTimeChange = (newStartTime: Date | null) => {
    // This function is no longer needed with slot-based booking
  };

  const validateTimes = (): string | null => {
    if (selectedSlots.length === 0) {
      return 'Please select at least one time slot';
    }

    const totalDuration = calculateSlotsDuration(selectedSlots);

    if (totalDuration < minDurationMinutes) {
      return `Minimum booking duration is ${minDurationMinutes} minutes`;
    }

    // Mirror the mixed-tier duration check from toggleSlotSelection using per-slot constraints.
    const peakSlotsSelected = selectedSlots.filter(s => s.pricingTier === 'peak');
    const hasNonPeakSelected = selectedSlots.some(s => s.pricingTier !== 'peak');
    const isMixedTierSelected = peakSlotsSelected.length > 0 && hasNonPeakSelected;

    if (isMixedTierSelected) {
      const peakMax = peakSlotsSelected[0].scheduleConstraints?.maxBookingDurationMinutes ?? maxDurationMinutes;
      const peakMinutes = peakSlotsSelected.reduce(
        (sum, s) => sum + timeToMinutes(s.endTime) - timeToMinutes(s.startTime), 0
      );
      if (peakMinutes > peakMax) {
        const hours = Math.floor(peakMax / 60);
        const mins = peakMax % 60;
        const durationText = mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
        return `Maximum peak-time booking duration is ${durationText}`;
      }
    } else {
      const slotMax = selectedSlots[0]?.scheduleConstraints?.maxBookingDurationMinutes ?? maxDurationMinutes;
      if (totalDuration > slotMax) {
        const hours = Math.floor(slotMax / 60);
        const mins = slotMax % 60;
        const durationText = mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
        return `Maximum booking duration is ${durationText}`;
      }
    }

    return null;
  };

  // Guest management functions
  const addGuest = () => {
    const newGuest: BookingGuest = {
      type: 'member',
      memberId: null,
      guest: null
    };
    setGuests([...guests, newGuest]);
  };

  const removeGuest = (index: number) => {
    const newGuests = guests.filter((_, i) => i !== index);
    setGuests(newGuests);
  };

  const updateGuestType = (index: number, type: 'member' | 'guest') => {
    const newGuests = [...guests];
    newGuests[index] = {
      type,
      memberId: type === 'member' ? null : null,
      guest: type === 'guest' ? { name: '', email: '' } : null
    };
    setGuests(newGuests);
  };

  const updateGuestMember = (index: number, memberId: string | null) => {
    const newGuests = [...guests];
    newGuests[index] = {
      ...newGuests[index],
      memberId
    };
    setGuests(newGuests);
  };

  const updateGuestInfo = (index: number, field: 'name' | 'email', value: string) => {
    const newGuests = [...guests];
    const currentGuest = newGuests[index].guest || { name: '', email: '' };
    newGuests[index] = {
      ...newGuests[index],
      guest: {
        ...currentGuest,
        [field]: value
      }
    };
    setGuests(newGuests);
  };

  const validateGuests = (): string | null => {
    // Check if player count restrictions are enabled
    if (playerCountRestrictions.enabled) {
      const totalPlayers = 1 + guests.length; // 1 for host + guests

      if (totalPlayers < playerCountRestrictions.minPlayers) {
        return `Minimum ${playerCountRestrictions.minPlayers} player(s) required. Please add ${playerCountRestrictions.minPlayers - totalPlayers} more guest(s).`;
      }

      if (totalPlayers > playerCountRestrictions.maxPlayers) {
        return `Maximum ${playerCountRestrictions.maxPlayers} player(s) allowed. Please remove ${totalPlayers - playerCountRestrictions.maxPlayers} guest(s).`;
      }
    }

    // Validate guest data
    for (let i = 0; i < guests.length; i++) {
      const guest = guests[i];

      if (guest.type === 'member' && !guest.memberId) {
        return `Player ${i + 1}: Please select a member`;
      }

      if (guest.type === 'guest') {
        if (!guest.guest?.name || guest.guest.name.trim() === '') {
          return `Player ${i + 1}: Please enter player name`;
        }
        if (!guest.guest?.email || guest.guest.email.trim() === '') {
          return `Player ${i + 1}: Please enter player email`;
        }
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(guest.guest.email)) {
          return `Player ${i + 1}: Please enter a valid email address`;
        }
      }
    }

    return null;
  };

  const validateEquipment = (): string | null => {
    for (const { equipment, quantity } of selectedEquipment) {
      if (quantity < 1) {
        return `Equipment ${equipment.name}: Quantity must be at least 1`;
      }

      if (quantity > equipment.maxPerBooking) {
        return `Equipment ${equipment.name}: Maximum ${equipment.maxPerBooking} units allowed per booking`;
      }

      const availability = equipmentAvailability.get(equipment.id);
      if (availability && availability.available < quantity) {
        return `Equipment ${equipment.name}: Only ${availability.available} unit(s) available for the selected time. You requested ${quantity}.`;
      }
    }

    return null;
  };

  const TAX_RATE = 0.13; // 13% tax rate

  const calculateEquipmentCost = (equipment: Equipment, quantity: number): number => {
    if (selectedSlots.length === 0) return 0;

    const bookingDurationMinutes = calculateSlotsDuration(selectedSlots);
    const costPerUnit = equipment.costAmount;
    const unitDuration = equipment.costUnit;

    // Calculate proportional cost
    const cost = (costPerUnit / unitDuration) * bookingDurationMinutes * quantity;

    // Round to 2 decimal places
    return Math.round(cost * 100) / 100;
  };

  const calculateEquipmentTax = (equipment: Equipment, quantity: number): number => {
    const cost = calculateEquipmentCost(equipment, quantity);
    return Math.round(cost * TAX_RATE * 100) / 100;
  };

  const calculateEquipmentTotal = (equipment: Equipment, quantity: number): number => {
    const cost = calculateEquipmentCost(equipment, quantity);
    const tax = calculateEquipmentTax(equipment, quantity);
    return cost + tax;
  };

  const calculateTotalEquipmentCost = (): number => {
    return selectedEquipment.reduce((total, { equipment, quantity }) => {
      return total + calculateEquipmentCost(equipment, quantity);
    }, 0);
  };

  const calculateTotalEquipmentTax = (): number => {
    return selectedEquipment.reduce((total, { equipment, quantity }) => {
      return total + calculateEquipmentTax(equipment, quantity);
    }, 0);
  };

  const calculateTotalEquipmentWithTax = (): number => {
    return selectedEquipment.reduce((total, { equipment, quantity }) => {
      return total + calculateEquipmentTotal(equipment, quantity);
    }, 0);
  };

  const calculateTotalBookingCost = (): number => {
    let total = 0;

    // Add booking fees from feeBreakdown if available
    if (feeBreakdown) {
      total += feeBreakdown.grandTotal || 0;
    }

    // Add equipment costs (with tax)
    total += calculateTotalEquipmentWithTax();

    return total;
  };

  const formatDateForAPI = (date: Date): string => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleBookAmenity = async () => {
    if (!selectedMember || !amenity || !bookingDate || selectedSlots.length === 0) {
      setError('Please fill in all required fields and select time slots');
      return;
    }

    const timeValidationError = validateTimes();
    if (timeValidationError) {
      setError(timeValidationError);
      return;
    }

    // Validate booking window for selected member
    const bookingWindowError = validateBookingWindow();
    if (bookingWindowError) {
      setError(bookingWindowError);
      return;
    }

    // Validate tier access if amenity has tier restrictions
    const tierAccessError = validateTierAccess();
    if (tierAccessError) {
      setError(tierAccessError);
      return;
    }

    // Validate guests
    const guestValidationError = validateGuests();
    if (guestValidationError) {
      setError(guestValidationError);
      return;
    }

    // Validate equipment
    const equipmentValidationError = validateEquipment();
    if (equipmentValidationError) {
      setError(equipmentValidationError);
      return;
    }

    // All validations passed, show confirmation dialog
    setShowConfirmation(true);
  };

  const confirmBooking = async () => {
    if (!selectedMember || !bookingDate || selectedSlots.length === 0 || !amenity) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setShowConfirmation(false);

      const dateString = formatDateForAPI(bookingDate);

      // Get start and end times from selected slots
      const sortedSlots = [...selectedSlots].sort((a, b) => a.startTime.localeCompare(b.startTime));
      const startTime = sortedSlots[0].startTime;
      const endTime = sortedSlots[sortedSlots.length - 1].endTime;

      const bookingData: any = {
        bookingDate: dateString,
        startTime,
        endTime,
        notes: notes || undefined,
        memberProfileId: selectedMember.id
      };

      // Include guests if any are added
      if (guests.length > 0) {
        bookingData.guests = guests;
      }

      // Include equipment if any are selected
      if (selectedEquipment.length > 0) {
        bookingData.equipment = selectedEquipment.map(({ equipment, quantity }) => ({
          equipmentId: equipment.id,
          quantity
        }));
      }

      const response = await amenityAPI.createAmenityBooking(amenity.id, bookingData);

      // Close dialog immediately and then call onSuccess with the message
      onClose();

      // Delay the onSuccess call to allow dialog to close cleanly
      setTimeout(() => {
        onSuccess(response.data.message || 'Amenity booked successfully!');
      }, 100);

    } catch (error: any) {
      console.error('Error booking amenity:', error);
      const errorMessage = error.response?.data?.message || 'Failed to book amenity';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Validate booking window for selected member
  const validateBookingWindow = (): string | null => {
    if (!selectedMember || !amenity || !bookingDate || selectedSlots.length === 0) return null;

    const memberTier = selectedMember.membershipTier;
    if (!memberTier) {
      return 'Member does not have a membership tier assigned';
    }

    const tierName = memberTier.name;
    const bookingWindow = amenity.bookingWindows?.[tierName];

    if (!bookingWindow) {
      return `No booking window configured for tier "${tierName}". Contact an administrator.`;
    }

    // Check if booking date is beyond the advance booking window
    // The date picker already disables invalid dates, but we double-check here
    const dateString = formatDateForAPI(bookingDate); // YYYY-MM-DD
    const bookingDateOnly = new Date(dateString + 'T00:00:00');
    const maxDateOnly = new Date(maxBookingDate);
    maxDateOnly.setHours(0, 0, 0, 0);
    
    if (bookingDateOnly > maxDateOnly) {
      return `Your membership tier's booking window has been exceeded. Please select an earlier date.`;
    }

    return null;
  };

  // Validate tier access for the amenity
  const validateTierAccess = (): string | null => {
    if (!selectedMember || !amenity) return null;

    const memberTier = selectedMember.membershipTier;
    if (!memberTier) {
      return 'Member does not have a membership tier assigned';
    }

    // Check if amenity has tier restrictions through availableToTiers
    // Note: This check may need to be coordinated with backend validation
    // For now, we'll rely on the booking window presence as an indicator of access
    const tierName = memberTier.name;
    const bookingWindow = amenity.bookingWindows?.[tierName];

    if (!bookingWindow) {
      return `Member's tier "${tierName}" does not have access to this amenity. Please contact an administrator.`;
    }

    return null;
  };

  // Helper function to convert time string to minutes since midnight
  const timeToMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!amenity) return null;

  const today = new Date();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        disableEscapeKeyDown={true}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookOnlineIcon />
            <Box>
              <Typography variant="h6">
                {isMemberUser ? 'Book Amenity' : 'Book Amenity for Member'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {amenity.name} • {amenity.location}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent ref={dialogContentRef}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Member Selection */}
            <FormControl fullWidth>
              <Autocomplete
                options={memberProfiles}
                value={selectedMember}
                onChange={(event, newValue) => setSelectedMember(newValue)}
                disabled={isMemberUser}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                getOptionKey={(option) => option.id}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;

                  const searchTerm = inputValue.toLowerCase().trim();
                  if (searchTerm.length === 0) return options;

                  const filtered = options.filter(option => {
                    // Handle potential null/undefined values
                    const firstName = (option.firstName || '').toLowerCase();
                    const lastName = (option.lastName || '').toLowerCase();
                    const email = (option.email || '').toLowerCase();
                    const fullName = `${firstName} ${lastName}`.toLowerCase();

                    // Check if any field contains the search term
                    const matches = fullName.includes(searchTerm) ||
                      email.includes(searchTerm) ||
                      firstName.includes(searchTerm) ||
                      lastName.includes(searchTerm) ||
                      searchTerm.split(' ').every(term =>
                        fullName.includes(term) || email.includes(term)
                      );

                    return matches;
                  });

                  return filtered;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body1">
                        {option.firstName} {option.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email} • {option.membershipTier?.name || 'No Tier'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Member"
                    placeholder="Search members..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={loadingMembers ? "Loading members..." : "No members found"}
              />
            </FormControl>

            {/* Date Selection */}
            <Box>
              <DatePicker
                label="Booking Date"
                value={bookingDate}
                onChange={(newValue) => {
                  setBookingDate(newValue);
                  // Clear selected slots when date changes
                  setSelectedSlots([]);
                }}
                minDate={today}
                maxDate={maxBookingDate}
                shouldDisableDate={shouldDisableDate}
                enableAccessibleFieldDOMStructure={false}
                closeOnSelect={true}
                slots={{
                  textField: TextField
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    },
                    helperText: selectedMember && amenity.bookingWindows?.[selectedMember.membershipTier?.name || '']
                      ? (() => {
                        const window = amenity.bookingWindows[selectedMember.membershipTier!.name];
                        return window.unlimited
                          ? 'Unlimited advance booking allowed'
                          : `You can book up to ${window.advanceBookingDays} days in advance from now`;
                      })()
                      : 'Select a member to see booking window'
                  }
                }}
              />
            </Box>

            {/* Available Time Slots */}
            {bookingDate && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Available Time Slots
                  {loadingSlots && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </Typography>

                {availableSlots.length === 0 && !loadingSlots && (
                  <Alert severity="info">
                    No available time slots for this date. Please select a different date.
                  </Alert>
                )}

                {availableSlots.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Select consecutive slots (each slot is {minDurationMinutes} minutes).
                      Max duration: {maxDurationMinutes} minutes.
                    </Typography>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' },
                      gap: 1,
                      maxHeight: { xs: '220px', sm: '300px' },
                      overflowY: 'auto',
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}>
                      {availableSlots.map((slot, index) => {
                        const isSelected = selectedSlots.some(
                          s => s.startTime === slot.startTime && s.endTime === slot.endTime
                        );
                        return (
                          <Chip
                            key={`${slot.startTime}-${slot.endTime}-${index}`}
                            label={`${slot.startTime} - ${slot.endTime}`}
                            onClick={() => toggleSlotSelection(slot)}
                            color={isSelected ? 'primary' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            icon={isSelected ? <CheckCircleIcon /> : <AccessTimeIcon />}
                            sx={{
                              minWidth: '140px',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.dark' : 'action.hover'
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Guest Management Section */}
            {selectedMember && bookingDate && selectedSlots.length > 0 && (
              <Box ref={guestSectionRef}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      <GroupAddIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Additional Players
                    </Typography>
                    {playerCountRestrictions.enabled ? (
                      <Typography variant="caption" color="text.secondary">
                        {playerCountRestrictions.minPlayers === playerCountRestrictions.maxPlayers
                          ? `Exactly ${playerCountRestrictions.maxPlayers} total player(s) required (including host)`
                          : `${playerCountRestrictions.minPlayers} to ${playerCountRestrictions.maxPlayers} total players required (including host)`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Add players to your booking (optional)
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addGuest}
                    disabled={playerCountRestrictions.enabled && 1 + guests.length >= playerCountRestrictions.maxPlayers}
                    sx={{ mt: { xs: 2, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Add Player
                  </Button>
                </Box>

                {/* Current player count indicator */}
                {playerCountRestrictions.enabled && (
                  <Alert
                    severity={
                      1 + guests.length < playerCountRestrictions.minPlayers ? 'warning' :
                        1 + guests.length > playerCountRestrictions.maxPlayers ? 'error' : 'success'
                    }
                    sx={{ mb: 2 }}
                  >
                    Current: {1 + guests.length} player(s)
                    (1 host + {guests.length} player{guests.length !== 1 ? 's' : ''})
                  </Alert>
                )}

                {/* Host info */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Host</Typography>
                  <Typography variant="body2">
                    {selectedMember.firstName} {selectedMember.lastName}
                  </Typography>
                </Box>

                {/* Guest list */}
                {guests.map((guest, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">Player {index + 1}</Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeGuest(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Player Type</InputLabel>
                      <Select
                        value={guest.type}
                        label="Player Type"
                        onChange={(e) => updateGuestType(index, e.target.value as 'member' | 'guest')}
                      >
                        <MenuItem value="member">Club Member</MenuItem>
                        <MenuItem value="guest">External Guest</MenuItem>
                      </Select>
                    </FormControl>

                    {guest.type === 'member' ? (
                      <Autocomplete
                        options={memberProfiles.filter(m =>
                          m.id !== selectedMember.id && // Exclude host
                          !guests.some((g, i) => i !== index && g.type === 'member' && g.memberId === m.id) // Exclude already selected members
                        )}
                        value={memberProfiles.find(m => m.id === guest.memberId) || null}
                        onChange={(event, newValue) => updateGuestMember(index, newValue?.id || null)}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          return (
                            <Box component="li" key={key} {...otherProps}>
                              <Box>
                                <Typography variant="body2">
                                  {option.firstName} {option.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.email} • {option.membershipTier?.name || 'No Tier'}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Member"
                            placeholder="Search members..."
                          />
                        )}
                        noOptionsText="No members available"
                      />
                    ) : (
                      <Box>
                        <TextField
                          fullWidth
                          label="Player Name"
                          value={guest.guest?.name || ''}
                          onChange={(e) => updateGuestInfo(index, 'name', e.target.value)}
                          sx={{ mb: 2 }}
                          required
                        />
                        <TextField
                          fullWidth
                          label="Player Email"
                          type="email"
                          value={guest.guest?.email || ''}
                          onChange={(e) => updateGuestInfo(index, 'email', e.target.value)}
                          required
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Equipment Selection */}
            {availableEquipment.length > 0 && selectedMember && bookingDate && selectedSlots.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Equipment (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Select equipment to rent for this booking. Cost is calculated based on booking duration.
                </Typography>

                {selectedEquipment.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {selectedEquipment.map(({ equipment, quantity }, index) => {
                      const availability = equipmentAvailability.get(equipment.id);
                      const cost = calculateEquipmentCost(equipment, quantity);
                      const tax = calculateEquipmentTax(equipment, quantity);
                      const total = cost + tax;
                      const durationMinutes = calculateSlotsDuration(selectedSlots);

                      return (
                        <Box
                          key={equipment.id}
                          sx={{
                            mb: 2,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2">{equipment.name}</Typography>
                              {equipment.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {equipment.description}
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedEquipment(selectedEquipment.filter((_, i) => i !== index));
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                            <TextField
                              type="number"
                              label="Quantity"
                              value={quantity}
                              onChange={(e) => {
                                const newQty = parseInt(e.target.value) || 1;
                                const updated = [...selectedEquipment];
                                updated[index] = { ...updated[index], quantity: Math.max(1, Math.min(newQty, equipment.maxPerBooking)) };
                                setSelectedEquipment(updated);
                              }}
                              inputProps={{
                                min: 1,
                                max: equipment.maxPerBooking
                              }}
                              size="small"
                              sx={{ width: 100 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                <strong>Subtotal:</strong> ${cost.toFixed(2)} + ${tax.toFixed(2)} tax = ${total.toFixed(2)}
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {' '}(${equipment.costAmount} per {equipment.costUnit} min × {durationMinutes} min × {quantity} unit{quantity > 1 ? 's' : ''})
                                </Typography>
                              </Typography>
                              {availability && (
                                <Typography variant="caption" color={availability.available < quantity ? 'error' : 'success.main'}>
                                  {availability.available} of {availability.total} available
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                <Autocomplete
                  options={availableEquipment.filter(eq =>
                    !selectedEquipment.some(sel => sel.equipment.id === eq.id)
                  )}
                  getOptionLabel={(option) => `${option.name} - $${option.costAmount}/${option.costUnit}min`}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ${option.costAmount} per {option.costUnit} minutes • Max {option.maxPerBooking} per booking
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Equipment"
                      placeholder="Select equipment..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingEquipment ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedEquipment([...selectedEquipment, { equipment: newValue, quantity: 1 }]);
                    }
                  }}
                  value={null}
                  noOptionsText={loadingEquipment ? "Loading equipment..." : "No equipment available"}
                />
              </Box>
            )}

            {/* Notes - Hidden for member users */}
            {!isMemberUser && (
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this booking..."
              />
            )}

            {/* Booking Summary */}
            {selectedMember && bookingDate && selectedSlots.length > 0 && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Booking Summary:
                </Typography>
                <Typography variant="body2">
                  <strong>Member:</strong> {selectedMember.firstName} {selectedMember.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Amenity:</strong> {amenity.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {bookingDate.toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {selectedSlots[0]?.startTime} - {selectedSlots[selectedSlots.length - 1]?.endTime}
                </Typography>
                <Typography variant="body2">
                  <strong>Duration:</strong> {calculateSlotsDuration(selectedSlots)} minutes ({selectedSlots.length} slots)
                </Typography>

                {/* Per-Player Fee Breakdown */}
                {loadingFees ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Calculating fees...</Typography>
                  </Box>
                ) : feeBreakdown ? (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Fee Breakdown:
                    </Typography>

                    {/* Host Fee */}
                    <Typography variant="body2">
                      <strong>Booking fee for host ({selectedMember.firstName}):</strong>{' '}
                      {
                        <>
                          ${feeBreakdown.host.totalAmount.toFixed(2)}
                          {feeBreakdown.host.discount > 0 && (
                            <Typography component="span" variant="caption" color="success.main">
                              {' '}(includes ${feeBreakdown.host.discount.toFixed(2)} Junior discount)
                            </Typography>
                          )}
                          {feeBreakdown.host.guestFees > 0 && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}(incl. ${feeBreakdown.host.guestFees.toFixed(2)} player fees)
                            </Typography>
                          )}
                        </>
                      }
                    </Typography>

                    {/* Member Guest Fees */}
                    {feeBreakdown.memberGuests && feeBreakdown.memberGuests.length > 0 && (
                      feeBreakdown.memberGuests.map((guest: any, index: number) => (
                        <Typography key={index} variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Booking fee for Player {index + 1} ({guest.name}):</strong>{' '}
                          {guest.shouldPay ? (
                            <>
                              ${guest.totalAmount.toFixed(2)}
                              {guest.discount > 0 && (
                                <Typography component="span" variant="caption" color="success.main">
                                  {' '}(includes ${guest.discount.toFixed(2)} Junior discount)
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography component="span">N/A (Free) - {guest.reason}</Typography>
                          )}
                        </Typography>
                      ))
                    )}

                    {/* External Guest Info */}
                    {feeBreakdown.externalGuests && feeBreakdown.externalGuests.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>External players:</strong> {feeBreakdown.externalGuests.length} × ${feeBreakdown.guestFeePerPerson.toFixed(2)} = ${(feeBreakdown.externalGuests.length * feeBreakdown.guestFeePerPerson).toFixed(2)} (charged to host)
                      </Typography>
                    )}
                  </Box>
                ) : null}

                {selectedEquipment.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Equipment:</strong>
                    </Typography>
                    {selectedEquipment.map(({ equipment, quantity }) => {
                      const cost = calculateEquipmentCost(equipment, quantity);
                      const tax = calculateEquipmentTax(equipment, quantity);
                      const total = cost + tax;
                      return (
                        <Typography key={equipment.id} variant="body2" sx={{ ml: 2 }}>
                          • {equipment.name} × {quantity}: ${cost.toFixed(2)} + ${tax.toFixed(2)} tax = ${total.toFixed(2)}
                        </Typography>
                      );
                    })}
                  
                    <Typography variant="body2">
                      <strong>Total Equipment Cost:</strong> ${calculateTotalEquipmentWithTax().toFixed(2)}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleBookAmenity}
            variant="contained"
            disabled={!selectedMember || !bookingDate || selectedSlots.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <BookOnlineIcon />}
          >
            {loading ? 'Booking...' : 'Book Amenity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookOnlineIcon color="primary" />
            <Typography variant="h6">Confirm Booking</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body1">
              Please confirm you want to book the following:
            </Typography>

            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Amenity:</strong> {amenity?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Location:</strong> {amenity?.location}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {bookingDate?.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Time:</strong> {selectedSlots.length > 0 && `${selectedSlots[0]?.startTime} - ${selectedSlots[selectedSlots.length - 1]?.endTime}`}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Duration:</strong> {calculateSlotsDuration(selectedSlots)} minutes
              </Typography>

              {selectedEquipment.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Equipment:</strong>
                  </Typography>
                  {selectedEquipment.map(({ equipment, quantity }) => (
                    <Typography key={equipment.id} variant="body2" sx={{ ml: 2 }}>
                      • {equipment.name} × {quantity}
                    </Typography>
                  ))}
                </Box>
              )}

              {guests.length > 0 && (
                <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                  <strong>Players:</strong> {guests.length} additional player{guests.length !== 1 ? 's' : ''}
                </Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Per-Player Fee Breakdown in Confirmation */}
              {feeBreakdown ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Fee Breakdown:
                  </Typography>

                  {/* Host Fee */}
                  <Typography variant="body2" gutterBottom>
                    <strong>Host ({selectedMember?.firstName}):</strong>{' '}
                    {
                      <>
                        ${feeBreakdown.host.totalAmount.toFixed(2)}
                        {feeBreakdown.host.discount > 0 && (
                          <Typography component="span" variant="caption" color="success.main">
                            {' '}(with Junior discount)
                          </Typography>
                        )}
                      </>
                    }
                  </Typography>

                  {/* Member Guest Fees */}
                  {feeBreakdown.memberGuests && feeBreakdown.memberGuests.map((guest: any, index: number) => (
                    <Typography key={index} variant="body2" gutterBottom>
                      <strong>Player {index + 1} ({guest.name}):</strong>{' '}
                      {guest.shouldPay ? (
                        <>
                          ${guest.totalAmount.toFixed(2)}
                          {guest.discount > 0 && (
                            <Typography component="span" variant="caption" color="success.main">
                              {' '}(with Junior discount)
                            </Typography>
                          )}
                        </>
                      ) : (
                        'N/A (Free)'
                      )}
                    </Typography>
                  ))}

                  {/* External Guest Info */}
                  {feeBreakdown.externalGuests && feeBreakdown.externalGuests.length > 0 && (
                    <Typography variant="body2" gutterBottom>
                      <strong>External players:</strong> {feeBreakdown.externalGuests.length} × ${feeBreakdown.guestFeePerPerson.toFixed(2)} (charged to host)
                    </Typography>
                  )}
                </Box>
                ) : null}
                <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                  <strong>Equipment Cost (incl. tax):</strong> ${calculateTotalEquipmentWithTax().toFixed(2)}
                </Typography>
              

              {feeBreakdown && feeBreakdown.grandTotal > 0 && (
                <Typography variant="body2" gutterBottom sx={{ mt: 1, fontWeight: 'bold' }}>
                  <strong>Grand Total:</strong> ${(feeBreakdown.grandTotal + calculateTotalEquipmentWithTax()).toFixed(2)}
                </Typography>
              )}
            </Box>

            <Alert severity="info" icon={<AccessTimeIcon />}>
              <Typography variant="body2">
                <strong>Cancellation Policy:</strong> Cancellations are allowed up to 24 hours prior to your booking time.
                Failure to cancel within this timeframe may result in a penalty.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmation(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmBooking}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {loading ? 'Booking...' : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BookAmenityDialog;
